import React, { useState, useEffect } from 'react';
import { X, FileText, Loader, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const TermsModal = ({ isOpen, onClose, onAccept }) => {
  const [termsContent, setTermsContent] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchTerms();
    }
  }, [isOpen]);

  const fetchTerms = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/api/public/terms-and-conditions`);
      if (response.ok) {
        const data = await response.json();
        setTermsContent(data.terms || '');
      } else {
        console.error('Failed to fetch terms, status:', response.status);
        setTermsContent('Terms and conditions are currently unavailable. Please contact us for more information.');
      }
    } catch (error) {
      console.error('Error fetching terms:', error);
      setTermsContent('Terms and conditions are currently unavailable. Please contact us for more information.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 flex items-center justify-between">
          <div className="flex items-center">
            <FileText className="w-6 h-6 mr-3" />
            <h2 className="text-2xl font-bold">Booking Conditions & Terms</h2>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto p-6 flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="w-8 h-8 animate-spin text-blue-600" />
              <span className="ml-3 text-gray-600">Loading terms and conditions...</span>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="prose prose-sm max-w-none">
                <pre className="whitespace-pre-wrap font-sans text-sm text-gray-700 leading-relaxed">
{termsContent}
                </pre>
              </div>

              {/* Agreement Notice */}
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-6">
                <div className="flex">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-yellow-800">
                    <strong>Important:</strong> By clicking "I Accept" and completing your booking, 
                    you acknowledge that you have read, understood, and agree to be bound by these 
                    Terms and Conditions, including our cancellation and refund policies.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t bg-gray-50 p-6 flex flex-col sm:flex-row gap-3 justify-end">
          <Button
            onClick={onClose}
            variant="outline"
            className="order-2 sm:order-1"
            disabled={loading}
          >
            Decline
          </Button>
          <Button
            onClick={onAccept}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 order-1 sm:order-2"
            disabled={loading}
          >
            I Accept Terms & Conditions
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TermsModal;
